import * as Tone from 'tone';
import type { FaseTrack } from '../audio/beats/fases';
import { createAudioEngine } from '../audio/engine';
import { BEATS_PER_BAR } from '../audio/plan';
import { playTrack, type LoadedTrack, type TrackPlayback } from '../audio/track';
import { loadOffsetMs, mergeOffset, saveOffsetMs, type CalibrationResult } from '../core/calibration';
import {
  STAB_BEATS,
  approachBeats,
  dipDepthAt,
  gradePosition,
  isGuided,
  progressAt,
  sizeAt,
  type LevelSpec,
  type Note,
  type Song,
} from '../core/chart';
import { createClock } from '../core/clock';
import { DESKTOP_WINDOWS, MOBILE_WINDOWS } from '../core/judge';
import { boardSeed } from '../core/progress';
import { accuracy, applyJudgement, createRunState, meanErrorMs, passed, type RunState } from '../core/score';
import type { Beat, InputMode, Seconds, Verdict, Windows } from '../core/types';
import { beginRun, endRun, recordObject } from '../net/telemetry';
import { isMilestone } from '../render/burst';
import { lookOf, type LookId } from '../render/look';
import { createScene, type GateView, type LandingView, type PuckView } from '../render/scene';
import { createStage } from '../render/stage';
import { PASS_BEATS, QUEUE, clamp01, fieldPoint, type Point } from '../render/view';
import { runCalibration } from './calibrate';
import { faseChart, faseLook, faseTitle, nextFase } from './campaign';
import { cueAt, type Coach, type Cue, type Device } from './cue';
import {
  REGROW_SECONDS,
  SHRINK_SECONDS,
  createAim,
  easeSize,
  fieldBounds,
  followPlace,
  liftOf,
  missBy,
  pointingOf,
  puckRadius,
  tipPoint,
  type SizeEase,
} from './puck';
import { createRound, isRetry, type Call, type Round } from './round';
import {
  UNLOCK_EVENTS,
  acceptPress,
  isHoldKey,
  keyHold,
  matchesRelease,
  pointerHold,
  startsOnPress,
  type HoldInput,
} from './hold';

export interface GameHud {
  state: RunState;
  verdict: Verdict | null;
  deltaMs: number;
  holding: boolean;
  running: boolean;
  cleared: boolean;
  note: Note | null;
  seed: string;
  bpm: number;
  beat: Beat;
  fase: FaseTrack;
  music: 'track' | 'band' | null;
  loading: boolean;
  outputLatencyMs: number;
  offsetMs: number;
  calibrating: boolean;
  milestone: number | null;
  syncMs: number | null;
  syncNote: number | null;
}

export interface Game {
  start(): Promise<void>;
  restart(): Promise<void>;
  setFase(fase: FaseTrack): void;
  calibrate(): Promise<CalibrationResult | null>;
  destroy(): void;
}

export interface GameOptions {
  seed: string;
  fase: FaseTrack;
  onHud(hud: GameHud): void;
  onClear?(fase: FaseTrack): void;
  onCue?(cue: Cue | null): void;
  coach?(fase: FaseTrack): boolean;
  blocked?(): boolean;
  music?(fase: FaseTrack): Promise<LoadedTrack | null>;
  lookOf?(fase: FaseTrack): LookId;
  onLook?(look: LookId): void;
  probe?: boolean;
}

export interface ProbeNote {
  readonly index: number;
  readonly apexMs: number;
  readonly holdMs: number;
  readonly x: number;
  readonly y: number;
}

export interface Probe {
  readonly running: boolean;
  readonly over: boolean;
  readonly lives: number;
  readonly judged: number;
  readonly notes: readonly ProbeNote[];
}

interface Landing {
  readonly note: Note;
  readonly verdict: Verdict;
  readonly x: number;
  readonly y: number;
  readonly size: number;
  readonly at: Seconds;
  readonly points: number;
  readonly multiplier: number;
  readonly milestone: boolean;
  retiredAt: Seconds | null;
}

interface Guide {
  readonly index: number | null;
  readonly from: Point;
  readonly at: Seconds;
}

const TAIL_SECONDS = 1.4;
const LANDINGS_KEPT = 4;
const DIP_VISIBLE = 0.03;
const DIP_SHAKE = 5;
const BLIND_FROM = 0.4;
const BLIND_TO = 0.92;
const MAX_DT_MS = 60_000;
const MAX_RELEASE_DIST = 8;
const FADE_SECONDS = 0.25;

function rounded(value: number, step: number, limit: number): number {
  return Math.max(-limit, Math.min(limit, Math.round(value / step) * step));
}

function isCoarsePointer(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
}

function deviceOf(pointerType: string): Device {
  return pointerType === 'touch' ? 'touch' : 'mouse';
}

function prefersReducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export async function createGame(mount: HTMLElement, options: GameOptions): Promise<Game> {
  const lookFor = (f: FaseTrack): LookId => options.lookOf?.(f) ?? faseLook(f);
  let fase = options.fase;
  let lookId = lookFor(fase);
  const stage = await createStage(mount, lookOf(lookId).palette.bg);
  let scene = createScene(stage, lookOf(lookId));
  const engine = createAudioEngine();
  const windows: Windows = isCoarsePointer() ? MOBILE_WINDOWS : DESKTOP_WINDOWS;
  let offsetMs = loadOffsetMs();
  let clock = createClock(Tone.getContext().rawContext, { extraLatencyMs: offsetMs });
  const audio = (): AudioContext => Tone.getContext().rawContext as unknown as AudioContext;

  let chart = faseChart(options.seed, fase, null);
  let song: Song = chart.song;
  let spec: LevelSpec = chart.spec;
  let play: TrackPlayback | null = null;
  let fade: GainNode | null = null;
  let music: 'track' | 'band' | null = null;
  let loading = false;
  let state = createRunState();
  let round: Round = createRound(song, 0, windows);
  let running = false;
  let starting = false;
  let calibrating = false;
  let cleared = false;
  let coaching = false;
  let lastBeat: Beat = 0;
  let lastVerdict: Verdict | null = null;
  let lastDeltaMs = 0;
  let syncMs: number | null = null;
  let syncNote: number | null = null;
  let cueKey: string | null = null;
  let hold: HoldInput | null = null;
  let mode: InputMode = 'classic';
  let device: Device = isCoarsePointer() ? 'touch' : 'key';
  let landings: Landing[] = [];
  const aim = createAim();
  let guide: Guide | null = null;
  let retried: Note | null = null;
  let sizeEase: SizeEase | null = null;

  const board = (): string => boardSeed(options.seed, fase.id);
  const title = (): string => faseTitle(fase).toUpperCase();

  const applyLook = (): void => {
    const next = lookFor(fase);
    if (next === lookId) return;
    lookId = next;
    const look = lookOf(next);
    scene.destroy();
    stage.app.renderer.background.color = look.palette.bg;
    scene = createScene(stage, look);
    options.onLook?.(next);
  };

  const prefetch = (f: FaseTrack): void => {
    void options.music?.(f).catch(() => null);
  };

  const stopMusic = (when?: Seconds): void => {
    engine.stop(when);
    if (play === null) return;
    const at = Math.max(when ?? 0, audio().currentTime);
    fade?.gain.setTargetAtTime(0, at, FADE_SECONDS / 3);
    play.stop(at + FADE_SECONDS);
    play = null;
    fade = null;
  };

  const startMusic = (track: LoadedTrack | null): Seconds => {
    const timed = chart.timed;
    if (track === null || timed === null) {
      music = 'band';
      const marks = song.notes.map((note) => note.apexBeat);
      return engine.startRun({ bpm: song.bpm, kit: song.kit, seed: board(), marks });
    }
    music = 'track';
    const ctx = audio();
    const gain = ctx.createGain();
    gain.connect(Tone.getDestination().output.output as unknown as AudioNode);
    fade = gain;
    play = playTrack(track, timed, ctx, gain);
    return play.beatZero;
  };

  const publish = (now: Seconds = clock.now()): void => {
    options.onHud({
      state,
      verdict: lastVerdict,
      deltaMs: lastDeltaMs,
      holding: round.holdNote !== null,
      running,
      cleared,
      note: round.holdNote ?? song.notes[round.cursor] ?? null,
      seed: board(),
      bpm: song.bpm,
      beat: running ? Math.max(0, round.beatAt(now)) : 0,
      fase,
      music,
      loading,
      outputLatencyMs: clock.outputLatency * 1000,
      offsetMs,
      calibrating,
      milestone: state.milestone,
      syncMs,
      syncNote,
    });
  };

  const glide = (g: Guide, now: Seconds): Point =>
    followPlace(g.from, g.index === null ? aim.placeAt(now) : (song.notes[g.index] as Note), now - g.at);

  const steer = (now: Seconds): Point => {
    if (spec.still <= 0) return aim.placeAt(now);
    const note = round.holdNote ?? song.notes[round.cursor] ?? null;
    const index = note !== null && isGuided(note, spec.still) ? note.index : null;
    if (guide === null || guide.index !== index) {
      guide = { index, from: guide === null ? aim.placeAt(now) : glide(guide, now), at: now };
    }
    return glide(guide, now);
  };

  const coachOf = (now: Seconds): Coach | null =>
    coaching && fase.coach > 0
      ? { notes: fase.coach, still: spec.still, holding: hold !== null, device, place: steer(now), judged: (i) => round.judged(i) }
      : null;

  const showCue = (beat: Beat, now: Seconds): void => {
    const cue = running ? cueAt(beat, song.notes, title(), coachOf(now), retried) : null;
    const key = cue?.key ?? null;
    if (key === cueKey) return;
    cueKey = key;
    options.onCue?.(cue);
  };

  const retire = (at: Seconds): void => {
    for (const landing of landings) if (landing.retiredAt === null) landing.retiredAt = at;
  };

  const drawnSize = (now: Seconds): number =>
    easeSize(sizeEase, puckRadius(round.holdNote, round.beatAt(now)), now);

  const finish = (at: Seconds): void => {
    running = false;
    hold = null;
    round.drop();
    cleared = passed(state, fase.safe);
    if (play === null) engine.stop(engine.now() + TAIL_SECONDS);
    else if (!cleared) stopMusic(audio().currentTime + TAIL_SECONDS);
    endRun();
    if (cleared) options.onClear?.(fase);
    publish(at);
  };

  const settle = (
    call: Call,
    verdict: Verdict,
    place: Point,
    size: number,
    distance: number | null,
    follow: number | null,
  ): void => {
    const { note, deltaMs, at } = call;
    const timing = call.verdict;
    const before = state.score;
    const lastMultiplier = state.multiplier;
    state = applyJudgement(
      state,
      { verdict, deltaMs, riserIndex: note.index },
      note.holdBeats / BEATS_PER_BAR,
      1,
      fase.safe,
    );
    const heard = play === null ? engine.heardAt(note.apexBeat) : play.timeOf(note.apexBeat);
    syncMs = heard === null ? null : (heard - round.apexTime(note)) * 1000;
    syncNote = note.index;
    recordObject({
      riser: note.index,
      length_beats: note.holdBeats,
      shape: note.curve,
      blind: note.blind,
      stab: note.holdBeats <= STAB_BEATS,
      dips: note.dips.length,
      timing,
      verdict,
      dt_ms: rounded(deltaMs, 0.1, MAX_DT_MS),
      follow,
      release_dist: distance === null ? null : rounded(distance, 0.001, MAX_RELEASE_DIST),
      on_target: distance === null ? null : distance <= note.size,
      t_ms: Math.max(0, Math.round((at - round.runStart) * 1000)),
    });
    retire(at);
    landings.push({
      note,
      verdict,
      x: place.x,
      y: place.y,
      size,
      at,
      points: state.score - before,
      multiplier: state.multiplier,
      milestone: isMilestone(state.milestone, lastMultiplier, state.multiplier),
      retiredAt: null,
    });
    if (landings.length > LANDINGS_KEPT) landings = landings.slice(-LANDINGS_KEPT);
    lastVerdict = verdict;
    lastDeltaMs = deltaMs;
    if (state.over) finish(at);
    else publish(at);
  };

  const followOf = (input: HoldInput | null): number | null => (input === null ? null : input.source === 'pointer' ? 1 : 0);

  const press = (at: Seconds, input: HoldInput, repeat: boolean): void => {
    if (!running || !acceptPress(hold, input, repeat)) return;
    hold = input;
    const from = retried === null ? null : drawnSize(at);
    const note = round.press(at);
    retried = null;
    if (from !== null) sizeEase = { from, at, span: REGROW_SECONDS };
    if (note !== null) retire(at);
    publish(at);
  };

  const release = (at: Seconds, input: HoldInput): void => {
    if (!matchesRelease(hold, input)) return;
    const place = steer(at);
    hold = null;
    const drawn = drawnSize(at);
    const call = running ? round.release(at) : null;
    if (call === null) {
      round.drop();
      publish(at);
      return;
    }
    if (isRetry(call)) {
      retried = call.retry;
      sizeEase = { from: drawn, at, span: SHRINK_SECONDS };
      publish(at);
      return;
    }
    const note = call.note;
    if (isGuided(note, spec.still)) {
      settle(call, call.verdict, place, sizeAt(note, round.beatAt(at)), null, followOf(input));
      return;
    }
    const distance = missBy(place, note);
    const verdict = gradePosition(call.verdict, distance, note.size, spec.slack);
    settle(call, verdict, place, sizeAt(note, round.beatAt(at)), distance, followOf(input));
  };

  const sweep = (now: Seconds): void => {
    for (const call of round.sweep(now)) {
      if (!running) return;
      const note = call.note;
      if (note === retried) retried = null;
      if (!call.held) {
        settle(call, 'miss', { x: note.x, y: note.y }, 0, null, null);
        continue;
      }
      const place = steer(now);
      settle(call, 'miss', place, sizeAt(note, round.beatAt(now)), null, followOf(hold));
    }
  };

  const attach = (now: Seconds): void => {
    if (hold === null) return;
    const note = round.attach(now);
    if (note === null) return;
    retire(now);
    publish(now);
  };

  const puckView = (beat: Beat, now: Seconds): PuckView | null => {
    if (!running) return null;
    const note = round.holdNote;
    const place = steer(now);
    const p = note === null ? 0 : progressAt(note, beat);
    const depth = note === null ? 0 : dipDepthAt(note, clamp01(p));
    return {
      x: place.x,
      y: place.y,
      size: easeSize(sizeEase, puckRadius(note, beat), now),
      shape: note?.shape ?? 'circle',
      rotation: note?.rotation ?? 0,
      shake: depth > DIP_VISIBLE ? clamp01(depth * DIP_SHAKE) : 0,
      visible: note === null || !(note.blind && p > BLIND_FROM && p < BLIND_TO),
      holding: hold !== null,
    };
  };

  const gatesAt = (beat: Beat): GateView[] => {
    const gates: GateView[] = [];
    if (!running) return gates;
    let current = false;
    for (let i = round.cursor; i < song.notes.length && gates.length < QUEUE; i++) {
      const note = song.notes[i] as Note;
      const lead = note.apexBeat - beat;
      if (lead > approachBeats(note) || round.judged(note.index) || lead < -PASS_BEATS) continue;
      const first = !current && lead >= 0;
      if (first) current = true;
      gates.push({ note, lead, current: first });
    }
    return gates;
  };

  const landingViews = (now: Seconds): LandingView[] =>
    landings.map((landing) => ({
      note: landing.note,
      verdict: landing.verdict,
      x: landing.x,
      y: landing.y,
      size: landing.size,
      age: now - landing.at,
      retired: landing.retiredAt === null ? null : now - landing.retiredAt,
      points: landing.points,
      multiplier: landing.multiplier,
      milestone: landing.milestone,
    }));

  const fromChrome = (event: Event): boolean =>
    event.target instanceof Element && event.target.closest('[data-ui]') !== null;

  const offLimits = (event: Event): boolean => options.blocked?.() === true || fromChrome(event);

  const track = (event: PointerEvent, downAt: Seconds | null = null): void => {
    const view = { width: stage.width, height: stage.height };
    const lift = liftOf(event.pointerType);
    const at = tipPoint(stage.canvas.getBoundingClientRect(), view, event.clientX + lift.x, event.clientY + lift.y);
    const pointing = pointingOf(event.pointerType);
    if (downAt !== null) aim.down(event.pointerId, pointing, at, fieldBounds(view), downAt);
    else aim.move(event.pointerId, pointing, at, fieldBounds(view));
  };

  const capture = (id: number): void => {
    try {
      stage.canvas.setPointerCapture(id);
    } catch {
      return;
    }
  };

  let keyBlocked = false;
  let pointerBlocked: number | null = null;
  let armed: number | null = null;
  const unlock = (): void => engine.unlock();

  const onKeyDown = (event: KeyboardEvent): void => {
    if (calibrating || !isHoldKey(event.code)) return;
    if (!event.repeat) keyBlocked = offLimits(event);
    if (keyBlocked) return;
    event.preventDefault();
    if (!event.repeat) device = 'key';
    if (!running) {
      if (!event.repeat) void begin('classic');
      return;
    }
    press(clock.atEvent(event), keyHold(), event.repeat);
  };

  const onKeyUp = (event: KeyboardEvent): void => {
    if (calibrating || !isHoldKey(event.code)) return;
    if (keyBlocked) {
      keyBlocked = false;
      return;
    }
    event.preventDefault();
    release(clock.atEvent(event), keyHold());
  };

  const onPointerDown = (event: PointerEvent): void => {
    if (calibrating || (event.pointerType === 'mouse' && event.button !== 0)) return;
    if (offLimits(event)) {
      pointerBlocked = event.pointerId;
      if (!fromChrome(event) && document.activeElement instanceof HTMLElement) document.activeElement.blur();
      return;
    }
    device = deviceOf(event.pointerType);
    if (!running) {
      if (hold !== null) return;
      if (startsOnPress(event.pointerType)) void begin('aim');
      else armed = event.pointerId;
      return;
    }
    const input = pointerHold(event.pointerId);
    if (!acceptPress(hold, input, false)) return;
    const at = clock.atEvent(event);
    track(event, at);
    if (event.target === stage.canvas) capture(event.pointerId);
    press(at, input, false);
  };

  const onPointerMove = (event: PointerEvent): void => {
    if (hold === null || hold.source === 'key' ? event.isPrimary : matchesRelease(hold, pointerHold(event.pointerId))) track(event);
  };

  const onPointerUp = (event: PointerEvent): void => {
    if (pointerBlocked === event.pointerId) {
      pointerBlocked = null;
      return;
    }
    if (calibrating) return;
    if (armed === event.pointerId) {
      armed = null;
      if (!running) void begin('aim');
      return;
    }
    const input = pointerHold(event.pointerId);
    if (!matchesRelease(hold, input)) return;
    track(event);
    const at = clock.atEvent(event);
    release(at, input);
    aim.up(event.pointerId, at);
  };

  const onPointerLost = (event: PointerEvent): void => {
    if (pointerBlocked === event.pointerId) pointerBlocked = null;
    if (armed === event.pointerId) armed = null;
    const at = clock.atEvent(event);
    release(at, pointerHold(event.pointerId));
    aim.up(event.pointerId, at);
  };

  addEventListener('keydown', onKeyDown);
  addEventListener('keyup', onKeyUp);
  addEventListener('pointerdown', onPointerDown);
  addEventListener('pointermove', onPointerMove);
  addEventListener('pointerup', onPointerUp);
  addEventListener('pointercancel', onPointerLost);
  stage.canvas.addEventListener('lostpointercapture', onPointerLost);
  for (const type of UNLOCK_EVENTS) addEventListener(type, unlock, true);

  stage.ticker.add(() => {
    const now = clock.now();
    const beat = running ? round.beatAt(now) : lastBeat;
    if (running) {
      if (play === null) engine.ensureMusic(beat);
      sweep(now);
      if (running) attach(now);
      if (running && beat >= song.endBeat) finish(now);
    }
    lastBeat = beat;
    const cursor = running ? 'none' : '';
    if (stage.canvas.style.cursor !== cursor) stage.canvas.style.cursor = cursor;
    showCue(beat, now);
    scene.draw({
      gates: gatesAt(beat),
      puck: puckView(beat, now),
      landings: landingViews(now),
      aim: null,
      beat,
      time: now,
      reduced: prefersReducedMotion(),
      level: fase.fase,
    });
  });

  const reset = (): void => {
    round = createRound(song, 0, windows);
    hold = null;
    aim.reset();
    guide = null;
    retried = null;
    sizeEase = null;
    landings = [];
    lastVerdict = null;
    lastDeltaMs = 0;
    lastBeat = 0;
    syncMs = null;
    syncNote = null;
    cleared = false;
  };

  async function start(by: InputMode = mode): Promise<void> {
    if (starting) return;
    starting = true;
    try {
      await engine.ready();
      loading = true;
      publish();
      const loaded = (await options.music?.(fase).catch(() => null)) ?? null;
      loading = false;
      chart = faseChart(options.seed, fase, loaded?.calendar ?? null);
      song = chart.song;
      spec = chart.spec;
      state = createRunState();
      reset();
      mode = by;
      coaching = options.coach?.(fase) === true;
      if (play !== null) stopMusic();
      round = createRound(song, startMusic(loaded), windows);
      beginRun(board(), mode);
      running = true;
      publish();
    } finally {
      loading = false;
      starting = false;
    }
  }

  async function restart(by: InputMode = mode): Promise<void> {
    if (starting) return;
    if (running) endRun();
    running = false;
    stopMusic();
    await start(by);
  }

  const choose = (next: FaseTrack): void => {
    fase = next;
    chart = faseChart(options.seed, fase, null);
    song = chart.song;
    spec = chart.spec;
    music = null;
    applyLook();
    prefetch(fase);
  };

  async function begin(by: InputMode): Promise<void> {
    if (starting) return;
    const next = cleared ? nextFase(fase) : null;
    if (next !== null) choose(next);
    await restart(by);
  }

  async function calibrate(): Promise<CalibrationResult | null> {
    if (calibrating || starting) return null;
    calibrating = true;
    try {
      if (running) {
        running = false;
        hold = null;
        round.drop();
        stopMusic();
        endRun();
      }
      await engine.ready();
      publish();
      const result = await runCalibration({
        context: Tone.getContext().rawContext,
        clock,
        host: mount.ownerDocument.body,
      });
      if (result === null) return null;
      offsetMs = saveOffsetMs(mergeOffset(offsetMs, result.offsetMs));
      clock = createClock(Tone.getContext().rawContext, { extraLatencyMs: offsetMs });
      return { ...result, offsetMs };
    } finally {
      calibrating = false;
      publish();
    }
  }

  const probe = (): Probe => {
    const now = clock.now();
    const rect = stage.canvas.getBoundingClientRect();
    const view = { width: stage.width, height: stage.height };
    const ms = (beat: Beat): number => (round.timeOf(beat) - now) * 1000;
    const notes = running ? song.notes.slice(round.cursor).filter((note) => !round.judged(note.index) && note !== round.holdNote) : [];
    return {
      running,
      over: state.over,
      lives: state.lives,
      judged: state.judged,
      notes: notes.slice(0, QUEUE).map((note) => {
        const p = fieldPoint(view, note.x, note.y);
        return {
          index: note.index,
          apexMs: ms(note.apexBeat),
          holdMs: ms(note.apexBeat - note.holdBeats),
          x: rect.left + (p.x * rect.width) / view.width,
          y: rect.top + (p.y * rect.height) / view.height,
        };
      }),
    };
  };
  if (options.probe === true) Object.assign(globalThis, { __soltaProbe: probe });

  prefetch(fase);
  publish();

  return {
    start: () => start(),
    restart: () => restart(),
    setFase(next) {
      if (running || starting) return;
      if (next.id !== fase.id) stopMusic();
      choose(next);
      state = createRunState();
      reset();
      publish();
    },
    calibrate,
    destroy() {
      removeEventListener('keydown', onKeyDown);
      removeEventListener('keyup', onKeyUp);
      removeEventListener('pointerdown', onPointerDown);
      removeEventListener('pointermove', onPointerMove);
      removeEventListener('pointerup', onPointerUp);
      removeEventListener('pointercancel', onPointerLost);
      stage.canvas.removeEventListener('lostpointercapture', onPointerLost);
      for (const type of UNLOCK_EVENTS) removeEventListener(type, unlock, true);
      stopMusic();
      engine.dispose();
      scene.destroy();
      stage.destroy();
    },
  };
}

export { accuracy, meanErrorMs };
